import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { CalendarClock, Type, UsersRound } from 'lucide-react';
import { Button, Placeholder } from '@/shared/ui/primitives';

import { useSearchBarState, useTelegramBackButton } from '@/shared/hooks';

import { Page } from '@/app/Page';
import {
  ActionSection,
  QueryBoundary,
  SearchBar,
  SectionRow,
  SelectionBottomBar,
  SortSheet,
} from '@/shared/ui';
import {
  ClientModal,
  clientsQueryOptions,
  useClientSelection,
  useClientSort,
  useFilteredClients,
  useMarkVisit,
  useMarkVisits,
  usePendingVisitClientIds,
  usePrefetchClientSort,
  type ClientFilterType,
  type ClientSortField,
} from '@/features/clients';
import type { Client } from '@/types';
import { getPassMeta } from '@/shared/lib/client';
import type { SortFieldOption } from '@/shared/lib/sort';
import { TEST_IDS } from '@/testIds';
import { paths, type ClientProfileLocationState } from '@/navigation/paths';
import { ClientsToolbar } from './index-page/ClientsToolbar';
import { ClientsList } from './index-page/ClientsList';
import { FilterChips } from './index-page/FilterChips';
import { SortChip } from './index-page/SortChip';
import { QuickPassEditor } from './index-page/QuickPassEditor';
import styles from './IndexPage.module.css';

const clientSortFields: SortFieldOption<ClientSortField>[] = [
  {
    label: 'Name',
    value: 'name',
    before: <Type size={16} aria-hidden="true" />,
  },
  {
    label: 'End date',
    value: 'endDate',
    before: <CalendarClock size={16} aria-hidden="true" />,
  },
];

const findSortLabel = (value: ClientSortField): string =>
  clientSortFields.find((field) => field.value === value)?.label ?? value;

export const IndexPage: FC = () => {
  usePrefetchClientSort();
  return (
    <Page back={false}>
      <QueryBoundary>
        <IndexPageContent />
      </QueryBoundary>
    </Page>
  );
};

const IndexPageContent: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: clients,
    isRefetchError,
    isFetching,
    refetch,
  } = useSuspenseQuery(clientsQueryOptions.list());

  const {
    searchQuery,
    setSearchQuery,
    isSearchVisible,
    openSearch,
    closeSearch,
    searchTriggerRef,
  } = useSearchBarState();
  const [editingPassId, setEditingPassId] = useState<string | null>(null);
  const [isPassEditorOpen, setIsPassEditorOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const selectedClient = clients.find((client) => client.id === selectedClientId) ?? null;
  const isModalOpen = selectedClient !== null;
  const [filterBy, setFilterBy] = useState<ClientFilterType>('all');
  const { sortBy, sortDirection, setSortBy, setSortDirection } = useClientSort(filterBy);
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false);
  const visitsInFlight = useRef(new Set<string>());
  const pendingVisitClientIds = usePendingVisitClientIds();

  const canMarkVisit = useCallback((client: Client): boolean => {
    const meta = getPassMeta(client.pass);
    return meta ? meta.isActive : false;
  }, []);

  const canRecordVisit = useCallback(
    (client: Client): boolean =>
      !isRefetchError &&
      !visitsInFlight.current.has(client.id) &&
      !pendingVisitClientIds.has(client.id) &&
      canMarkVisit(client),
    [isRefetchError, pendingVisitClientIds, canMarkVisit]
  );

  const {
    isSelectionMode,
    isExitingSelection,
    selectedClients,
    selectedCount,
    toggleClientSelection,
    handleClientLongPress,
    selectAllFromClients,
    startExitingSelection,
    exitSelectionMode,
  } = useClientSelection({ canMarkVisit: canRecordVisit });

  const filteredClients = useFilteredClients(clients, searchQuery, sortBy, sortDirection, filterBy);

  const openClient = useCallback(
    (client: Client) => {
      setIsPassEditorOpen(false);
      setSelectedClientId(client.id);
      void queryClient.prefetchQuery({
        ...clientsQueryOptions.detail(client.id),
        staleTime: 0,
        meta: { errorToast: false },
      });
    },
    [queryClient]
  );

  const toggleSelectableClient = useCallback(
    (id: string) => {
      if (!pendingVisitClientIds.has(id)) toggleClientSelection(id);
    },
    [pendingVisitClientIds, toggleClientSelection]
  );

  const closeModal = useCallback(() => {
    setIsPassEditorOpen(false);
    setSelectedClientId(null);
  }, []);

  const markVisitMutation = useMarkVisit();
  const markVisitsMutation = useMarkVisits();

  const handleAddPass = useCallback(() => {
    if (selectedClient) {
      closeModal();
      const state: ClientProfileLocationState = { openAddPass: true };
      void navigate(paths.clientProfile(selectedClient.id), { state });
    }
  }, [selectedClient, navigate, closeModal]);

  const openSortSheet = useCallback(() => {
    setIsSortSheetOpen(true);
  }, []);

  const closeSortSheet = useCallback(() => {
    setIsSortSheetOpen(false);
  }, []);

  const backButtonVisible = isModalOpen || isSearchVisible || isSortSheetOpen;
  const handleBack = useCallback(() => {
    if (isModalOpen) {
      closeModal();
      return;
    }
    if (isSortSheetOpen) {
      closeSortSheet();
      return;
    }
    if (isSearchVisible) {
      closeSearch();
    }
  }, [isModalOpen, isSortSheetOpen, isSearchVisible, closeModal, closeSortSheet, closeSearch]);

  useTelegramBackButton({ visible: backButtonVisible, onBack: handleBack });

  const handleSelectAll = useCallback(() => {
    const hasMarkableClients = selectAllFromClients(filteredClients);
    if (!hasMarkableClients) {
      navigator.vibrate?.(60);
    }
  }, [filteredClients, selectAllFromClients]);

  const handleMark = useCallback(async (): Promise<void> => {
    const batch = clients
      .filter((client) => selectedClients.has(client.id) && canRecordVisit(client))
      .map((client) => client.id);
    if (!batch.length) return;
    batch.forEach((id) => visitsInFlight.current.add(id));
    exitSelectionMode();
    try {
      await markVisitsMutation.mutateAsync({ clients: batch });
    } catch {
      // The mutation reports the error and refreshes server data.
    } finally {
      batch.forEach((id) => visitsInFlight.current.delete(id));
    }
  }, [clients, selectedClients, canRecordVisit, exitSelectionMode, markVisitsMutation]);

  const handleMarkVisit = useCallback(async (): Promise<void> => {
    if (!selectedClient || !canRecordVisit(selectedClient)) return;
    const id = selectedClient.id;
    visitsInFlight.current.add(id);
    closeModal();
    try {
      await markVisitMutation.mutateAsync({ id });
    } catch {
      // The mutation reports the error and refreshes server data.
    } finally {
      visitsInFlight.current.delete(id);
    }
  }, [selectedClient, canRecordVisit, closeModal, markVisitMutation]);

  const handleEdit = useCallback(() => {
    if (selectedClient) {
      void navigate(paths.clientProfile(selectedClient.id));
    }
    closeModal();
  }, [selectedClient, navigate, closeModal]);

  const searchBar = (
    <SearchBar
      triggerRef={searchTriggerRef}
      isVisible={isSearchVisible}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onClose={closeSearch}
      onOpen={openSearch}
    />
  );

  return (
    <>
      <ActionSection
        title={isSelectionMode ? `${selectedCount} selected` : 'Clients'}
        testId={TEST_IDS.clientsHeading}
        isActive={isSearchVisible}
        inactiveActions={
          <>
            <ClientsToolbar isSelectionMode={isSelectionMode} onSelectAll={handleSelectAll} />
            {searchBar}
          </>
        }
        activeActions={<>{searchBar}</>}
        inactiveSubActions={
          !isSelectionMode ? <FilterChips value={filterBy} onChange={setFilterBy} /> : null
        }
      >
        {isRefetchError && (
          <div role="alert" className={styles.refreshNotice}>
            <p>We couldn’t refresh client data. Refresh before recording more visits.</p>
            <Button variant="primary" size="sm" loading={isFetching} onClick={() => void refetch()}>
              Refresh clients
            </Button>
          </div>
        )}
        {!isSelectionMode && (
          <SectionRow
            className={styles.sortRow}
            label={`${filteredClients.length} ${
              filteredClients.length === 1 ? 'client' : 'clients'
            }`}
            labelTestId={TEST_IDS.clientsCount}
            action={
              <SortChip
                label={findSortLabel(sortBy)}
                direction={sortDirection}
                onClick={openSortSheet}
                testId={TEST_IDS.sortChip}
              />
            }
          />
        )}
        {filteredClients.length === 0 ? (
          <Placeholder
            title={clients.length === 0 ? 'No clients yet' : 'No clients found'}
            description={
              clients.length === 0
                ? 'Add your first client to manage passes and keep track of visits.'
                : 'Try a different name or clear your filters.'
            }
            action={
              clients.length === 0 ? (
                <Button
                  onClick={() => {
                    void navigate(paths.addClient());
                  }}
                >
                  Add client
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => {
                    setSearchQuery('');
                    setFilterBy('all');
                    closeSearch();
                  }}
                >
                  Clear filters
                </Button>
              )
            }
          >
            <UsersRound size={42} strokeWidth={1.4} aria-hidden="true" />
          </Placeholder>
        ) : (
          <ClientsList
            clients={filteredClients}
            onClick={openClient}
            onLongPress={handleClientLongPress}
            isSelectionMode={isSelectionMode}
            isExitingSelection={isExitingSelection}
            selectedClients={selectedClients}
            onToggleSelect={toggleSelectableClient}
            pendingVisitClientIds={pendingVisitClientIds}
            canMarkVisit={canRecordVisit}
          />
        )}
      </ActionSection>

      <ClientModal
        isOpen={isModalOpen}
        client={selectedClient}
        onClose={closeModal}
        onMarkVisit={() => void handleMarkVisit()}
        onEdit={handleEdit}
        onAddPass={handleAddPass}
        onEditPass={() => {
          if (selectedClient?.pass) {
            setEditingPassId(selectedClient.pass.id);
            setIsPassEditorOpen(true);
          }
        }}
        canMarkVisit={selectedClient ? canMarkVisit(selectedClient) : false}
        recordDisabled={isRefetchError}
        isLoading={selectedClient ? pendingVisitClientIds.has(selectedClient.id) : false}
      >
        {isModalOpen && selectedClient && editingPassId && (
          <QuickPassEditor
            key={`${selectedClient.id}:${editingPassId}`}
            isOpen={isPassEditorOpen}
            clientId={selectedClient.id}
            passId={editingPassId}
            onClose={() => setIsPassEditorOpen(false)}
          />
        )}
      </ClientModal>
      {isSelectionMode && (
        <SelectionBottomBar
          onStartCancel={startExitingSelection}
          onCancel={exitSelectionMode}
          onMark={() => void handleMark()}
          selectedCount={selectedCount}
          recordDisabled={isRefetchError}
        />
      )}

      <SortSheet
        isOpen={isSortSheetOpen}
        onClose={closeSortSheet}
        fields={clientSortFields}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortByChange={setSortBy}
        onSortDirectionChange={setSortDirection}
        ariaLabel="Sort clients"
        testId={TEST_IDS.sortSheet}
      />
    </>
  );
};
